import { Injectable } from '@angular/core';
import {
  Observable,
  Subject,
  combineLatest,
  map,
  BehaviorSubject,
  distinctUntilChanged,
  filter,
  pairwise,
} from 'rxjs';
import { ScreenSizeService } from 'src/app/services/screen-size.service';
import {
  GALLERY_VIEW_RELATED_VIEWS,
  SessionSharedDataService,
  SessionView,
} from 'src/app/services/session-shared-data.service';
import { Placement } from 'src/app/standalones/directives/pncl-tooltip.directive';
import { TooltipArrowPlacement } from 'src/app/ui/tooltip/tooltip.component';
import { UiService } from 'src/app/services/ui.service';
import { FLAGS } from 'src/app/services/flags.service';
import { FlagsService } from 'src/app/services/flags.service';
import { ProviderStateService } from 'src/app/services/provider-state.service';
import { PictureInPictureService } from './picture-in-picture.service';
export enum GalleryBarBottomSheetMenu {
  MAIN_MENU,
  REACTIONS_MENU,
}

@Injectable()
export class GalleryBottomBarStateService {
  private _openBottomSheetMenu$ = new Subject<GalleryBarBottomSheetMenu>();
  private _galleryBarWidth$ = new BehaviorSubject<number>(0);

  constructor(
    private sessionSharedDataService: SessionSharedDataService,
    private screenSizeService: ScreenSizeService,
    private uiService: UiService,
    private flagsService: FlagsService,
    private pictureInPictureService: PictureInPictureService,
    private providerStateService: ProviderStateService,
  ) {}

  galleryBarShown$ = combineLatest([
    this.sessionSharedDataService.sessionView.current$,
    this.uiService.isMobile,
    this.pictureInPictureService.isPIPActive$,
    this.providerStateService.callConnected$,
  ]).pipe(
    map(([currentView, isMobile, isPIPActive, isCallConnected]) =>
      this.calcIsGalleryBarShown(currentView.view, isMobile, isPIPActive, isCallConnected),
    ),
  );

  menuTooltipArrow$: Observable<TooltipArrowPlacement> =
    this.screenSizeService.renderMenuAsBottomSheet$.pipe(
      map((isBotttomSheet) => (isBotttomSheet ? 'bottom' : 'right')),
    );

  menuTooltipPlacement$: Observable<Placement> =
    this.screenSizeService.renderMenuAsBottomSheet$.pipe(
      map((isBotttomSheet) => (isBotttomSheet ? 'top-center' : 'center-start')),
    );

  openBottomSheetMenu$: Observable<GalleryBarBottomSheetMenu> = this._openBottomSheetMenu$;

  callDisconnected$ = this.providerStateService.callConnected$.pipe(
    pairwise(),
    filter(([prev, curr]) => prev === true && curr === false),
    map(() => undefined as void),
  );

  galleryBarWidth$ = this._galleryBarWidth$.pipe(distinctUntilChanged());

  openBottomSheetMenu(menu: GalleryBarBottomSheetMenu = GalleryBarBottomSheetMenu.MAIN_MENU) {
    this._openBottomSheetMenu$.next(menu);
  }

  set galleryBarWidth(width: number) {
    this._galleryBarWidth$.next(width);
  }

  /**
   * Calculates whether the gallery bar should be shown based on the current state
   */
  get isGalleryBarShown(): boolean {
    return this.calcIsGalleryBarShown(
      this.sessionSharedDataService.sessionView.getSessionView(),
      this.uiService.isMobile.getValue(),
      this.pictureInPictureService.isPIPActive,
      this.providerStateService.callConnectedValue,
    );
  }

  private calcIsGalleryBarShown(
    currentView: SessionView,
    isMobile: boolean,
    isPIPActive: boolean,
    isCallConnected: boolean,
  ): boolean {
    if (!this.flagsService.isFlagEnabled(FLAGS.NEW_GALLERY_BAR_UI) || !isCallConnected) {
      return false;
    }

    const isFullscreen = currentView === SessionView.FULLSCREEN;

    return (
      (GALLERY_VIEW_RELATED_VIEWS.includes(currentView) && !(isFullscreen && isMobile)) ||
      isPIPActive
    );
  }
}
